import {
  ConfirmModal,
  Loading,
  Table,
  TableRowMenuCell,
  ToggleAllButton,
} from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useExpandedRowKeys } from '@/hooks';
import { useMessage } from '@/providers';
import {
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
  getRecursiveArray,
  getRecursiveIds,
  isRS,
} from '@/utils';
import dayjs from 'dayjs';
import { groupBy, sortBy } from 'lodash-es';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useCreateExpenditurePurpose,
  useDeleteExpenditurePurpose,
  useExpenditurePurposeRecursive,
  useExpenditurePurposes,
  useUpdateExpenditurePurpose,
} from '../../hooks';
import {
  AccountingTypeLabelModifier,
  ExpenditurePurposeLevelLabelModifier,
  type ExpenditurePurposeModel,
} from '../../models';
import {
  DEFAULT_PAGINATION_SETTING,
  fillZeroToAccountType,
  fillZeroToExpenditurePurpose,
  getBudgetAgendaSortOrderDataSource,
} from '../../utils';
import { BudgetAgendaSortOrderModal } from '../BudgetAgendaSortOrderModal';
import { useDrawerState } from '../DrawerStateProvider';
import { ExpenditurePurposeFormDrawer } from './ExpenditurePurposeFormDrawer';

export const ExpenditurePurposesPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const expenditurePurposes = useExpenditurePurposes();
  const selected = expenditurePurposes.data?.find((d) => {
    if (drawerState.open && drawerState.type === 'expenditure-purpose') {
      if (drawerState.isEditing) {
        return d.id === drawerState.id;
      }
      return 'upperId' in drawerState ? d.id === drawerState.upperId : true;
    }
  });
  const expenditurePurposesRecursive = useExpenditurePurposeRecursive(
    drawerState.open
      ? drawerState.isEditing
        ? (selected?.upperId ?? '')
        : (selected?.id ?? '')
      : '',
  );
  const { mutateAsync: createMutateAsync } = useCreateExpenditurePurpose();
  const { mutateAsync: updateMutateAsync } = useUpdateExpenditurePurpose();
  const { mutateAsync: deleteMutateAsync } = useDeleteExpenditurePurpose();
  const editPermission = useMasterTablePermission(true);
  const {
    expandedRowKeys,
    setExpandedRowKeys,
    onExpand,
    clearExpandedRowKeys,
  } = useExpandedRowKeys();
  const [budgetAgendaSortOrderModalState, setBudgetAgendaSortOrderModalState] =
    useState<{
      open: boolean;
      level: number | null;
      accountingId: string | null;
      upperId: string | null;
    }>({
      open: false,
      level: null,
      accountingId: null,
      upperId: null,
    });

  const [expenditurePurposeDelete, setExpenditurePurposeDelete] =
    useState<z.infer<typeof ExpenditurePurposeModel> | null>(null);

  if (expenditurePurposes.isLoading || !expenditurePurposes.data) {
    return <Loading />;
  }

  if (expenditurePurposes.isError) {
    openMessage('error', t('failed-fetch-expenditure'));
  }

  const groupedExpenditurePurposes = Object.entries(
    groupBy(
      getRecursiveArray(
        expenditurePurposes.data.filter((d) => !d.upperId),
        expenditurePurposes.data,
      ),
      'accountingId',
    ),
  )
    .flatMap(([accountingId, data]) => {
      const accounting = data.find(
        (d) => d.accountingId === accountingId,
      )?.accounting;
      if (!accounting) {
        return [];
      }
      return {
        id: accountingId,
        code: accounting.code,
        type: accounting.type,
        name: accounting.name,
        data: sortBy(data, isRS() ? 'sortOrder' : 'code').map((v) => ({
          ...v,
          children: sortBy(v.children, isRS() ? 'sortOrder' : 'code').map(
            (m) => ({
              ...m,
              children: sortBy(m.children, isRS() ? 'sortOrder' : 'code'),
            }),
          ),
        })),
      };
    })
    .sort((a, b) => a.code - b.code);

  const openAllExpanded = () => {
    setExpandedRowKeys(
      groupedExpenditurePurposes.reduce<string[]>((acc, cur) => {
        acc.push(cur.id);
        acc.push(...getRecursiveIds(cur.data));

        return acc;
      }, []),
    );
  };

  return (
    <>
      <Table
        enableSticky={false}
        dataSource={groupedExpenditurePurposes}
        rowKey="id"
        title={() => (
          <ToggleAllButton
            openAll={openAllExpanded}
            closeAll={clearExpandedRowKeys}
          />
        )}
        columns={[
          {
            title: t('code'),
            key: 'code',
            dataIndex: 'code',
            width: TABLE_CODE_WIDTH,
            render: fillZeroToAccountType,
          },
          {
            title: t('accounting-type'),
            key: 'type',
            dataIndex: 'type',
            width: 150,
            render: (value) => AccountingTypeLabelModifier[value],
          },
          {
            title: t('accounting-name'),
            key: 'name',
            dataIndex: 'name',
          },
        ]}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            onExpand(expanded, record.id);
          },
          rowExpandable: (record) => record.data.length > 0,
          expandedRowRender: (record) => (
            <Table
              enableSticky={false}
              style={{ marginLeft: 80 }}
              dataSource={record.data}
              rowKey="id"
              columns={[
                {
                  title: t('code'),
                  key: 'code',
                  dataIndex: 'code',
                  width: 140,
                  render: (value, record) =>
                    fillZeroToExpenditurePurpose({
                      value,
                      isLevel2: record.level === 2,
                    }),
                },
                {
                  title: t('level'),
                  key: 'level',
                  dataIndex: 'level',
                  width: 100,
                  render: (value) =>
                    ExpenditurePurposeLevelLabelModifier[value],
                },
                {
                  title: t('name'),
                  key: 'name',
                  dataIndex: 'name',
                  width: 240,
                },
                {
                  title: t('start-fiscal-year'),
                  key: 'startYear',
                  dataIndex: 'startYear',
                  width: TABLE_YEAR_WIDTH,
                },
                {
                  title: t('end-fiscal-year'),
                  key: 'endYear',
                  dataIndex: 'endYear',
                  width: TABLE_YEAR_WIDTH,
                },
                {
                  title: t('created-datetime'),
                  key: 'createdAt',
                  dataIndex: 'createdAt',
                  width: TABLE_DATETIME_WIDTH,
                  render: (value) => datetimeFormat({ datetime: value }),
                },
                {
                  title: t('updated-datetime'),
                  key: 'updatedAt',
                  dataIndex: 'updatedAt',
                  width: TABLE_DATETIME_WIDTH,
                  render: (value) => datetimeFormat({ datetime: value }),
                },
                ...(editPermission.data
                  ? [
                      {
                        title: '',
                        key: 'actions',
                        fixed: 'right' as const,
                        width: TABLE_ROW_MENU_WIDTH,
                        onCell: () => ({
                          style: { minWidth: TABLE_ROW_MENU_WIDTH },
                        }),
                        render: (_, record) => (
                          <TableRowMenuCell
                            onEdit={() => {
                              setDrawerState({
                                open: true,
                                type: 'expenditure-purpose',
                                isEditing: true,
                                id: record.id,
                              });
                            }}
                            items={
                              !isRS()
                                ? [
                                    ...(record.level !== 3
                                      ? [
                                          {
                                            key: 'create',
                                            label: `${t('create-item', { item: ExpenditurePurposeLevelLabelModifier[record.level + 1] })}`,
                                            onClick: () => {
                                              setDrawerState({
                                                open: true,
                                                type: 'expenditure-purpose',
                                                isEditing: false,
                                                upperId: record.id,
                                              });
                                            },
                                          },
                                        ]
                                      : []),
                                    {
                                      key: 'budget-agenda-sort-order',
                                      label: t('budget-agenda-sort-order-edit'),
                                      onClick: () => {
                                        return setBudgetAgendaSortOrderModalState(
                                          {
                                            open: true,
                                            level: record.level,
                                            accountingId:
                                              record.accountingId ?? null,
                                            upperId: record.upperId,
                                          },
                                        );
                                      },
                                    },
                                    {
                                      key: 'delete',
                                      label: t('delete'),
                                      danger: true,
                                      onClick: () =>
                                        setExpenditurePurposeDelete(record),
                                    },
                                  ]
                                : [
                                    ...(record.level !== 3
                                      ? [
                                          {
                                            key: 'create',
                                            label: `${t('create-item', { item: ExpenditurePurposeLevelLabelModifier[record.level + 1] })}`,
                                            onClick: () => {
                                              setDrawerState({
                                                open: true,
                                                type: 'expenditure-purpose',
                                                isEditing: false,
                                                upperId: record.id,
                                              });
                                            },
                                          },
                                        ]
                                      : []),
                                    {
                                      key: 'delete',
                                      label: t('delete'),
                                      danger: true,
                                      onClick: () =>
                                        setExpenditurePurposeDelete(record),
                                    },
                                  ]
                            }
                          />
                        ),
                      },
                    ]
                  : []),
              ]}
              expandable={{
                expandedRowKeys,
                onExpand: (expanded, record) => {
                  onExpand(expanded, record.id);
                },
                rowExpandable: (record) => Boolean(record.children.length > 0),
              }}
              pagination={false}
            />
          ),
        }}
        pagination={DEFAULT_PAGINATION_SETTING}
      />

      <ExpenditurePurposeFormDrawer
        {...(drawerState.open && drawerState.type === 'expenditure-purpose'
          ? drawerState.isEditing
            ? {
                type: 'edit',
                initialValues: {
                  ...selected,
                  period: [
                    selected?.startYear
                      ? dayjs().year(selected.startYear)
                      : undefined,
                    selected?.endYear
                      ? dayjs().year(selected.endYear)
                      : undefined,
                  ],
                  upperIds: expenditurePurposesRecursive.data?.map((d) => d.id),
                },
              }
            : 'upperId' in drawerState
              ? {
                  type: 'new',
                  initialValues: {
                    level: selected?.level ? selected.level + 1 : undefined,
                    upperIds: expenditurePurposesRecursive.data?.map(
                      (d) => d.id,
                    ),
                  },
                }
              : { type: 'new' }
          : { type: 'new' })}
        onSubmit={async (values) => {
          if (!drawerState.open) {
            return null;
          }
          drawerState.isEditing
            ? await updateMutateAsync({ ...values, id: drawerState.id })
            : await createMutateAsync(values);

          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            (async () => {
              setDrawerState({ open: false });
            })(),
          ]);
        }}
      />
      <ConfirmModal
        open={expenditurePurposeDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(expenditurePurposeDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(expenditurePurposeDelete.id);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setExpenditurePurposeDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setExpenditurePurposeDelete(null)}
        skipCheck
      />
      {!isRS() && (
        <BudgetAgendaSortOrderModal
          type="expenditure-purpose"
          open={budgetAgendaSortOrderModalState.open}
          dataSource={getBudgetAgendaSortOrderDataSource({
            data: expenditurePurposes.data,
            budgetAgendaSortOrderModalState: {
              level: budgetAgendaSortOrderModalState.level,
              accountingId: budgetAgendaSortOrderModalState.accountingId,
              upperId: budgetAgendaSortOrderModalState.upperId,
            },
          })}
          onCancel={() =>
            setBudgetAgendaSortOrderModalState({
              open: false,
              level: null,
              accountingId: null,
              upperId: null,
            })
          }
        />
      )}
    </>
  );
};
